import { useState, useRef, useEffect, useMemo } from "react";
import { classNames } from "primereact/utils";
import { useHistory, useLocation, Route, Redirect } from "react-router-dom";
import AppTopbar from "../../AppTopbar";
import AppMenu from "../../AppMenu";
import AppInlineProfile from "../../AppInlineProfile";
import { Tooltip } from "primereact/tooltip";
import GetUserInfoService from "../../service/GetUserInfoService";
import logoWhiteHorizontal from "../images/logo-white-horizontal.png";
import "./components/UserDashboardHome/UserDashboardHome.css";
import UserDashboardHome from "./components/UserDashboardHome/UserDashboardHome";
import NetworkTable from "./components/NetworkTable/NetworkTable";
import EditProfileInfo from "./components/EditProfileInfo/EditProfileInfo";
import localeText from "../../locale";
import StatementTable from "./components/StatementTable/StatementTable";
import { Dialog } from "primereact/dialog";
import Coupons from "./components/Coupons/Coupons";
import ProductsList from "./components/ProductsList/ProductsList";
import Product from "./components/Dropshipping/Products/Product";
import NewProduct from "./components/NewProduct/NewProduct";
import DropShipping from "./components/Dropshipping/Dropshipping";
import Attributes from "./components/Attributes/Attributes";
import Orders from "./components/Orders/Orders";
import ExtracoinTransfer from "./components/ExtracoinTransfer/ExtracoinTransfer";
import ExtracoinIntents from "./components/ExtracoinIntents/ExtracoinIntents";
import WithdrawIntent from "./components/WithdrawIntent/WithdrawIntent";
import ConfirmDocument from "../../components/ConfirmDocument/ConfirmDocument";
import AdminBackoffice from "../AdminBackoffice/AdminBackoffice";
import handleCurrency from "../../utilities/CurrencySwitch";
import UserStore from "./components/UserStore/UserStore";
import FavoritedProducts from "./components/FavoritedProducts/FavoritedProducts";
import ExtraPay from "./components/ExtraPay/ExtraPay";
import ImportProducts from "./components/ImportProducts/ImportProducts";
import Web3Dashboard from "./components/web3Dashboard/web3Dashboard";
import { formatDate } from "../../utilities/formatDate";
import { useToggle } from "../../hooks/useToggle";
import { Button } from "primereact/button";
import RenewSubscription from "./components/RenewSubscription/RenewSubscription";
import OrderDetails from "./components/OrderDetails/OrderDetails";
import Joyride from "react-joyride";
import { useTutorialContext } from "../../contexts/TutorialContext";
import StoresMap from "./components/storesMap/index";
import EvaluateOrder from "./components/Orders/EvaluateOrder/EvaluateOrder";
import StoreTransactions from "./components/StoreTransactions/StoreTransactions";
import QrCodePay from "./components/QrCodePay/QrCodePay";
import FringeBenefit from "./components/FringeBenefit/FringeBenefit";
import FringeBenefitsList from "./components/FringeBenefitsList/FringeBenefitsList";
import FringeBenefitEdit from "./components/FringeBenefitEdit/FringeBenefitEdit";
import FringeBenefitsPurchases from "./components/FringeBenefitsPurchases/FringeBenefitsPurchases";
import FringeBenefitsSales from "./components/FringeBenefitsSales/FringeBenefitsSales";

const TooltipComponent = ({
  backProps,
  continuous,
  index,
  isLastStep,
  primaryProps,
  skipProps,
  closeProps,
  step,
  tooltipProps,
}) => {
  return (
    <div
      {...tooltipProps}
      className="bg-white"
      style={{
        maxWidth: 350,
        width: "100%",
        overflow: "hidden",
        borderRadius: 10,
      }}
    >
      <div className="flex justify-content-end">
        <button {...closeProps} className="border-none bg-white px-2 py-2">
          <i className="pi pi-times" />
        </button>
      </div>
      <div className="p-4">
        {step.title && <h3>{step.title}</h3>}
        {step.content && <div>{step.content}</div>}
      </div>
      <div className="p-4">
        <div className="flex justify-content-end">
          <div className="flex gap-2">
            {index > 0 && (
              <button {...backProps} className="p-button">
                {localeText("backAction")}
              </button>
            )}
            <button {...primaryProps} className="p-button">
              {continuous ? localeText("nextAction") : localeText("closeAction")} ({index + 1})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const UserDashboard = () => {
  const stepsManagerAndSeller = [
    {
      target: "#step-1",
      content: (
        <>
          <p>{localeText("welcomeToExtraconomy")}</p>
          <p>{localeText("ifYouWantToSellOnExtraconomy")}</p>
        </>
      ),
      data: {
        next: "/dashboard-editprofile",
        previous: null,
      },
      disableBeacon: true,
    },
    {
      target: "#step-2",
      content: (
        <>
          <p>{localeText("registerYourAddress")}</p>
          <p>{localeText("initiallyYouNeedToRegisterYourAddress")}</p>
        </>
      ),
      data: {
        previous: "/dashboard",
        next: "/dashboard-editprofile",
      },
    },
    {
      target: "#step-3",
      content: (
        <>
          <p>{localeText("fillInYourContactPhoneNumber")}</p>
          <p>{localeText("fillInYourContactNumber")}</p>
        </>
      ),
      data: {
        previous: "/dashboard-editprofile",
        next: "/dashboard-editprofile",
      },
    },
    {
      target: "#step-4",
      content: (
        <>
          <p>{localeText("fillInTheDetailsAboutTheShippingService")}</p>
        </>
      ),
      data: {
        previous: "/dashboard-editprofile",
        next: "/dashboard-editprofile",
      },
    },
    {
      target: "#step-5",
      content: (
        <>
          <p>{localeText("saveTheModifiedInformation")}</p>
          <p>{localeText("afterFillingInAllTheInformation")}</p>
        </>
      ),
      data: {
        previous: "/dashboard-editprofile",
        next: "/dashboard-userstore",
      },
    },
    {
      target: "#step-6",
      content: (
        <>
          <p>{localeText("onThisPageYouWillConfigure")}</p>
        </>
      ),
      data: {
        previous: "/dashboard-editprofile",
        next: "/dashboard-newproduct",
      },
    },
    {
      target: "#step-7",
      content: (
        <>
          <p>{localeText("finallyOnThisPageYouWillRegisterYourProducts")}</p>
        </>
      ),
      data: {
        previous: "/dashboard-userstore",
        next: "/dashboard-editprofile",
      },
    },
  ];

  const stepsManagerAndSellerMobile = [
    {
      target: "#step-1-mobile",
      content: (
        <>
          <p>{localeText("welcomeToExtraconomy")}</p>
          <p>{localeText("ifYouWantToSellOnExtraconomy")}</p>
        </>
      ),
      data: {
        next: "/dashboard-editprofile",
        previous: null,
      },
      disableBeacon: true,
    },
    {
      target: "#step-2",
      content: (
        <>
          <p>{localeText("registerYourAddress")}</p>
          <p>{localeText("initiallyYouNeedToRegisterYourAddress")}</p>
        </>
      ),
      data: {
        previous: "/dashboard",
        next: "/dashboard-editprofile",
      },
    },
    {
      target: "#step-3",
      content: (
        <>
          <p>{localeText("fillInYourContactPhoneNumber")}</p>
          <p>{localeText("fillInYourContactNumber")}</p>
        </>
      ),
      data: {
        previous: "/dashboard-editprofile",
        next: "/dashboard-editprofile",
      },
    },
    {
      target: "#step-4",
      content: (
        <>
          <p>{localeText("fillInTheDetailsAboutTheShippingService")}</p>
        </>
      ),
      data: {
        previous: "/dashboard-editprofile",
        next: "/dashboard-editprofile",
      },
    },
    {
      target: "#step-5-mobile",
      content: (
        <>
          <p>{localeText("saveTheModifiedInformation")}</p>
          <p>{localeText("afterFillingInAllTheInformation")}</p>
        </>
      ),
      data: {
        previous: "/dashboard-editprofile",
        next: "/dashboard-userstore",
      },
    },
    {
      target: "#step-6",
      content: (
        <>
          <p>{localeText("onThisPageYouWillConfigure")}</p>
        </>
      ),
      data: {
        previous: "/dashboard-editprofile",
        next: "/dashboard-newproduct",
      },
    },
    {
      target: "#step-7",
      content: (
        <>
          <p>{localeText("onThisPageYouWillConfigure")}</p>
        </>
      ),
      data: {
        previous: "/dashboard-userstore",
        next: "/dashboard-editprofile",
      },
    },
  ];

  const stepsPartner = [
    {
      target: "#step-1",
      content: (
        <>
          <p>{localeText("welcomeToExtraconomy")}</p>
          <p>{localeText("ifYouWantToSellOnExtraconomy")}</p>
        </>
      ),
      data: {
        next: "/dashboard-editprofile",
        previous: null,
      },
      disableBeacon: true,
    },
    {
      target: "#step-2",
      content: (
        <>
          <p>{localeText("registerYourAddress")}</p>
          <p>{localeText("initiallyYouNeedToRegisterYourAddress")}</p>
        </>
      ),
      data: {
        previous: "/dashboard",
        next: "/dashboard-editprofile",
      },
    },
    {
      target: "#step-3",
      content: (
        <>
          <p>{localeText("fillInYourContactPhoneNumber")}</p>
          <p>{localeText("fillInYourContactNumber")}</p>
        </>
      ),
      data: {
        previous: "/dashboard-editprofile",
        next: "/dashboard-editprofile",
      },
    },
    {
      target: "#step-4",
      content: (
        <>
          <p>{localeText("fillInTheDetailsAboutTheShippingService")}</p>
        </>
      ),
      data: {
        previous: "/dashboard-editprofile",
        next: "/dashboard-editprofile",
      },
    },
    {
      target: "#step-5",
      content: (
        <>
          <p>{localeText("saveTheModifiedInformation")}</p>
          <p>{localeText("afterFillingInAllTheInformation")}</p>
        </>
      ),
      data: {
        previous: "/dashboard-editprofile",
        next: "/dashboard-newproduct",
      },
    },
    {
      target: "#step-7",
      content: (
        <>
          <p>{localeText("onThisPageYouWillConfigure")}</p>
        </>
      ),
      data: {
        previous: "/dashboard-editprofile",
        next: "/dashboard-editprofile",
      },
    },
  ];

  const stepsPartnerMobile = [
    {
      target: "#step-1-mobile",
      content: (
        <>
          <p>{localeText("welcomeToExtraconomy")}</p>
          <p>{localeText("ifYouWantToSellOnExtraconomy")}</p>
        </>
      ),
      data: {
        next: "/dashboard-editprofile",
        previous: null,
      },
      disableBeacon: true,
    },
    {
      target: "#step-2",
      content: (
        <>
          <p>{localeText("registerYourAddress")}</p>
          <p>{localeText("initiallyYouNeedToRegisterYourAddress")}</p>
        </>
      ),
      data: {
        previous: "/dashboard",
        next: "/dashboard-editprofile",
      },
    },
    {
      target: "#step-3",
      content: (
        <>
          <p>{localeText("fillInYourContactPhoneNumber")}</p>
          <p>{localeText("fillInYourContactNumber")}</p>
        </>
      ),
      data: {
        previous: "/dashboard-editprofile",
        next: "/dashboard-editprofile",
      },
    },
    {
      target: "#step-4",
      content: (
        <>
          <p>{localeText("fillInTheDetailsAboutTheShippingService")}</p>
        </>
      ),
      data: {
        previous: "/dashboard-editprofile",
        next: "/dashboard-editprofile",
      },
    },
    {
      target: "#step-5",
      content: (
        <>
          <p>{localeText("saveTheModifiedInformation")}</p>
          <p>{localeText("afterFillingInAllTheInformation")}</p>
        </>
      ),
      data: {
        previous: "/dashboard-editprofile",
        next: "/dashboard-newproduct",
      },
    },
    {
      target: "#step-1-mobile",
      content: (
        <>
          <p>{localeText("finallyOnThisPageYouWillRegisterYourProducts")}</p>
        </>
      ),
      data: {
        previous: "/dashboard-editprofile",
        next: "/dashboard-editprofile",
      },
    },
  ];

  const [category, setCategory] = useState(null);
  const [menuActive, setMenuActive] = useState(false);
  const [overlayMenuActive, setOverlayMenuActive] = useState(false);
  const [topbarMenuActive, setTopbarMenuActive] = useState(false);
  const [staticMenuDesktopInactive, setStaticMenuDesktopInactive] = useState(false);
  const [staticMenuMobileActive, setStaticMenuMobileActive] = useState(false);
  const [activeTopbarItem, setActiveTopbarItem] = useState(null);
  const [inlineMenuActive, setInlineMenuActive] = useState(false);
  const [configActive, setConfigActive] = useState(false);
  const [userPlan, setUserPlan] = useState();
  const copyTooltipRef = useRef();

  const menuMode = "static";
  const darkMenu = true;
  const profileMode = "popup";
  const inputStyle = "outlined";
  const ripple = false;

  // API INFO
  const [userInfo, setUserInfo] = useState(null);
  const [userTypeInfo, setUserTypeInfo] = useState(null);
  const [walletInfo, setWalletInfo] = useState(null);
  const [extracoinInfo, setExtracoinInfo] = useState(null);
  const [currency, setCurrency] = useState("$");
  const [directsInfo, setDirectsInfo] = useState(null);
  const [allNodesInfo, setAllNodesInfo] = useState(null);
  const [directsArray, setDirectsArray] = useState([]);
  const [allNodesArray, setAllNodesArray] = useState([]);

  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [isLoadingSubscriptionInfo, setIsLoadingSubscriptionInfo] = useState(true);

  const [activationInfo, setActivationInfo] = useState(null);
  const [lifetimeBonusInfo, setLifetimeBonusInfo] = useState(null);
  const [extracoinPercentage, setExtracoinPercentage] = useState(null);
  const [loadingAdmin, setLoadingAdmin] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  const Navigate = useHistory();
  const location = useLocation();

  const userHasSubscription =
    userTypeInfo &&
    (/manager/i.test(userTypeInfo) ||
      /partner/i.test(userTypeInfo) ||
      /seller/i.test(userTypeInfo));

  let menuClick = false;
  let configClick = false;
  let topbarItemClick = false;
  let inlineMenuClick = false;

  const getCategory = (cat) => {
    setCategory(cat);
  };
  function useQuery() {
    const { search } = useLocation();

    return useMemo(() => new URLSearchParams(search), [search]);
  }

  function getProductServicesItems() {
    let items = [
      {
        label: localeText("new"),
        icon: "pi pi-fw pi-plus",
        to: "newproduct",
        id: "step-7",
      },
      { label: localeText("list"), icon: "pi pi-fw pi-list", to: "products" },
      { label: localeText("attributes"), icon: "pi pi-fw pi-tags", to: "attributes" },
      { label: localeText("favorites"), icon: "pi pi-fw pi-heart-fill", to: "favorites" },
      {
        label: localeText("importProducts"),
        icon: "pi pi-fw pi-file-import",
        to: "importproducts",
      },
      {
        label: localeText("yourStore"),
        icon: "pi pi-fw pi-shopping-bag",
        to: "userstore",
        id: "step-6",
      },
    ];

    if (["Seller", "Manager"].includes(userPlan)) {
      items = items.concat([
        {
          label: localeText("dropshipping"),
          icon: "pi pi-fw pi-shopping-cart",
          to: "dropshipping",
        },
      ]);
    }
    return items;
  }

  let query = useQuery();

  const menu = [
    {
      label: localeText("dashboard"),
      icon: "pi pi-fw pi-chart-line",
      items: [
        { label: localeText("userDashboard"), icon: "pi pi-fw pi-chart-line", to: "/dashboard" },
      ],
    },
    {
      label: localeText("physicalStore"),
      icon: "pi pi-fw pi-wallet",
      items:
        userTypeInfo === "Store" || userTypeInfo === "Manager"
          ? [
              {
                label: localeText("transactions"),
                icon: "pi pi-fw pi-shop",
                to: "/dashboard-store-transactions",
              },
              {
                label: localeText("payWithQrCode"),
                icon: "pi pi-fw pi-wallet",
                to: "/dashboard-store-pay",
              },
            ]
          : [
              {
                label: localeText("payWithQrCode"),
                icon: "pi pi-fw pi-wallet",
                to: "/dashboard-store-pay",
              },
            ],
    },
    {
      label: localeText("wallet"),
      icon: "pi pi-fw pi-wallet",
      items: [
        {
          label: localeText("statement"),
          icon: "pi pi-fw pi-file",
          to: "/dashboard-walletStatement",
        },
        {
          label: localeText("withdraw"),
          icon: "pi pi-fw pi-money-bill",
          to: "/dashboard-withdrawintent",
        },
        { label: "Extrapay", icon: "pi pi-fw pi-chart-line", to: "/dashboard-extrapay" },
      ],
    },
    {
      label: "Extracredit",
      icon: "pi pi-fw pi-wallet",
      items: [
        {
          label: localeText("statement"),
          icon: "pi pi-fw pi-file",
          to: "/dashboard-extracoinStatement",
        },
        {
          label: localeText("transferRequests"),
          icon: "pi pi-fw pi-question-circle",
          to: "/dashboard-extracoinintents",
        },
        {
          label: localeText("transfer"),
          icon: "pi pi-fw pi-arrows-h",
          to: "/dashboard-extracointransfer",
        },
      ],
    },
    {
      label: "Bonus ads",
      icon: "pi pi-fw pi-wallet",
      items: [
        {
          label: localeText("statement"),
          icon: "pi pi-fw pi-file",
          to: "/dashboard-extrabonusStatement",
        },
      ],
    },
    {
      label: localeText("bonus"),
      icon: "pi pi-fw pi-wallet",
      items: [
        {
          label: localeText("statement"),
          icon: "pi pi-fw pi-file",
          to: "/dashboard-bonusStatement",
        },
      ],
    },
    {
      label: localeText("coupons"),
      icon: "pi pi-fw pi-box",
      items: [{ label: localeText("list"), icon: "pi pi-fw pi-list", to: "/dashboard-coupons" }],
    },
    {
      label: "Fringe Benefit",
      icon: "pi pi-fw pi-box",
      items: [
        {
          label: localeText("new"),
          icon: "pi pi-fw pi-plus",
          to: "dashboard-fringe-benefit",
        },
        {
          label: localeText("list"),
          icon: "pi pi-fw pi-list",
          to: "dashboard-list-fringe-benefit",
        },
        {
          label: localeText("sales"),
          icon: "pi pi-fw pi-money-bill",
          to: "/dashboard-sales-fringe-benefit",
        },
        {
          label: localeText("purchases"),
          icon: "pi pi-fw pi-shopping-bag",
          to: "/dashboard-purchases-fringe-benefit",
        },
      ],
    },
    {
      label: localeText("productServices"),
      icon: "pi pi-fw pi-box",
      items: getProductServicesItems().map((item) => ({ ...item, to: `/dashboard-${item.to}` })),
    },
    {
      label: localeText("sales"),
      icon: "pi pi-fw pi-money-bill",
      items: [{ label: localeText("list"), icon: "pi pi-fw pi-list", to: "/dashboard-sales" }],
    },
    {
      label: localeText("purchases"),
      icon: "pi pi-fw pi-shopping-bag",
      items: [{ label: localeText("list"), icon: "pi pi-fw pi-list", to: "/dashboard-purchases" }],
    },
    {
      label: localeText("network"),
      icon: "pi pi-fw pi-sitemap",
      items: [
        {
          label: localeText("directs"),
          icon: "pi pi-fw pi-sitemap",
          to: "/dashboard-networkdirects",
        },
        { label: localeText("all"), icon: "pi pi-fw pi-table", to: "/dashboard-networkall" },
      ],
    },
    userHasSubscription && {
      label: localeText("subscription"),
      icon: "pi pi-fw pi-wallet",
      items: [
        {
          label: localeText("renewSubscription"),
          icon: "pi pi-fw pi-file",
          to: "/dashboard-renewsubscription",
        },
      ],
    },
    {
      label: "Crypto",
      icon: "pi pi-fw pi-chart-line",
      items: [{ label: "Crypto", icon: "pi pi-fw pi-bitcoin", to: "/dashboard-web3dashboard" }],
    },
    {
      label: localeText("StoresMap"),
      icon: "pi pi-fw pi-map",
      items: [{ label: localeText("map"), icon: "pi pi-fw pi-map", to: "/dashboard-storesmap" }],
    },
  ];

  useEffect(() => {
    copyTooltipRef && copyTooltipRef.current && copyTooltipRef.current.updateTargetEvents();
  }, [location]);

  const getUserInfoService = new GetUserInfoService();

  const {
    setAsFalse: setNotVisiblePopUp,
    setAsTrue: setVisiblePopUp,
    value: isVisiblePopUp,
  } = useToggle();

  const {
    setAsFalse: setNotVisiblePopUpExpired,
    setAsTrue: setVisiblePopUpExpired,
    value: isVisiblePopUpExpired,
  } = useToggle();

  const popUpLeftSubscriptionExpiration = () => {
    if (
      subscriptionInfo?.subscriptionDaysLeft <= 10 &&
      subscriptionInfo?.subscriptionDaysLeft >= 0 &&
      "dashboard/renewsubscription" !== location.pathname
    ) {
      setVisiblePopUp();
    } else if (
      subscriptionInfo?.subscriptionDaysLeft < 0 &&
      "dashboard/renewsubscription" !== location.pathname
    ) {
      setVisiblePopUpExpired();
    } else {
      setNotVisiblePopUp();
    }
  };

  useEffect(() => {
    if (!isLoadingSubscriptionInfo) popUpLeftSubscriptionExpiration(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingSubscriptionInfo, location]);

  useEffect(() => {
    let cancel = false;
    async function fetchData() {
      if (localStorage.getItem("__extraconomy_token")) {
        setLoadingAdmin(true);

        const resType = await getUserInfoService.getUserTypeInfo(
          localStorage.getItem("__extraconomy_username")
        );

        if (cancel) return;

        if (resType.messageCode !== 200001) {
          setLoadingAdmin(false);
        } else {
          setUserTypeInfo(resType.result);

          if (resType.result === "Admin") {
            setIsAdmin(true);
            setLoadingAdmin(false);
          } else {
            try {
              // User info
              getUserInfoService
                .getUserInfo(
                  localStorage.getItem("__extraconomy_token"),
                  localStorage.getItem("__extraconomy_username")
                )
                .then((res) => {
                  if (res.messageCode === 200001) {
                    setUserPlan(res.result.plan);
                    setUserInfo(res.result.user);
                  } else {
                    localStorage.removeItem("__extraconomy_token");
                    localStorage.removeItem("__extraconomy_username");
                    Navigate.replace("/login", {
                      from: `${location.pathname}${location?.search ? location?.search : ""}`,
                    });
                  }
                });
              // Wallet info
              await getUserInfoService
                .getWalletInfo(
                  localStorage.getItem("__extraconomy_token"),
                  localStorage.getItem("__extraconomy_username")
                )
                .then((res) => {
                  if (res.messageCode === 200001) {
                    setWalletInfo(res.result);
                    setCurrency(handleCurrency(res.result.currency));
                  } else {
                    localStorage.removeItem("__extraconomy_token");
                    localStorage.removeItem("__extraconomy_username");
                    Navigate.replace("/login", {
                      from: `${location.pathname}${location?.search ? location?.search : ""}`,
                    });
                  }
                });

              // Extracredit info
              getUserInfoService
                .getExtracoinInfo(
                  localStorage.getItem("__extraconomy_token"),
                  localStorage.getItem("__extraconomy_username")
                )
                .then((res) => {
                  if (res.messageCode === 200001) setExtracoinInfo(res.result);
                  else {
                    localStorage.removeItem("__extraconomy_token");
                    localStorage.removeItem("__extraconomy_username");
                    Navigate.replace("/login", {
                      from: `${location.pathname}${location?.search ? location?.search : ""}`,
                    });
                  }
                });
              // Directs in economic circle
              getUserInfoService
                .getNetworkDirectsInfo(
                  localStorage.getItem("__extraconomy_token"),
                  localStorage.getItem("__extraconomy_username")
                )
                .then((res) => {
                  if (res.messageCode === 200001) {
                    setDirectsInfo(res.result);
                    const arr = [];

                    res.result.forEach((element) => {
                      arr.push(element);
                    });

                    setDirectsArray(arr);
                  } else {
                    localStorage.removeItem("__extraconomy_token");
                    localStorage.removeItem("__extraconomy_username");
                    Navigate.replace("/login", {
                      from: `${location.pathname}${location?.search ? location?.search : ""}`,
                    });
                  }
                });
              // All nodes in economic circle
              getUserInfoService
                .getNetworkAllInfo(
                  localStorage.getItem("__extraconomy_token"),
                  localStorage.getItem("__extraconomy_username")
                )
                .then((res) => {
                  if (res.messageCode === 200001) {
                    setAllNodesInfo(res.result);
                    const arr = [];

                    res.result.forEach((element) => {
                      arr.push(element);
                    });

                    setAllNodesArray(arr);
                  } else {
                    localStorage.removeItem("__extraconomy_token");
                    localStorage.removeItem("__extraconomy_username");
                    Navigate.replace("/login", {
                      from: `${location.pathname}${location?.search ? location?.search : ""}`,
                    });
                  }
                });
              // User Subscription info
              if (
                /manager/i.test(resType.result) ||
                /partner/i.test(resType.result) ||
                /seller/i.test(resType.result)
              )
                getUserInfoService
                  .getSubscriptionInfo(
                    localStorage.getItem("__extraconomy_token"),
                    localStorage.getItem("__extraconomy_username")
                  )
                  .then((res) => {
                    if (res.messageCode === 200001) {
                      setSubscriptionInfo({
                        ...res.result,
                        subscriptionFormattedData: `${formatDate(new Date(res.result.dateBegin))} -
                    ${formatDate(new Date(res.result.dateEnd))}`,
                        subscriptionDaysLeft:
                          ((new Date(res.result.dateEnd) - Date.now()) / 86400000) | 0,
                      });
                    } else {
                      localStorage.removeItem("__extraconomy_token");
                      localStorage.removeItem("__extraconomy_username");
                      Navigate.replace("/login", {
                        from: `${location.pathname}${location.search}`,
                      });
                    }
                  })
                  .finally(() => setIsLoadingSubscriptionInfo(false));
              // User activation info
              getUserInfoService
                .getActivationInfo(
                  localStorage.getItem("__extraconomy_token"),
                  localStorage.getItem("__extraconomy_username")
                )
                .then((res) => {
                  if (res.messageCode === 200001) setActivationInfo(res.result);
                  else {
                    localStorage.removeItem("__extraconomy_token");
                    localStorage.removeItem("__extraconomy_username");
                    Navigate.replace("/login", {
                      from: `${location.pathname}${location?.search ? location?.search : ""}`,
                    });
                  }
                });
              // Lifetime bonus of wallet
              getUserInfoService
                .getWalletLifetimeBonus(
                  localStorage.getItem("__extraconomy_token"),
                  localStorage.getItem("__extraconomy_username")
                )
                .then((res) => {
                  if (res.messageCode === 200001) setLifetimeBonusInfo(res.result);
                  else {
                    localStorage.removeItem("__extraconomy_token");
                    localStorage.removeItem("__extraconomy_username");
                    Navigate.replace("/login", {
                      from: `${location.pathname}${location?.search ? location?.search : ""}`,
                    });
                  }
                });
              // Extracredit percentage usage credit
              getUserInfoService
                .getExtracoinPercentage(
                  localStorage.getItem("__extraconomy_token"),
                  localStorage.getItem("__extraconomy_username")
                )
                .then((res) => {
                  if (res.messageCode === 200001) setExtracoinPercentage(res.result);
                  else {
                    localStorage.removeItem("__extraconomy_token");
                    localStorage.removeItem("__extraconomy_username");
                    Navigate.replace("/login", {
                      from: `${location.pathname}${location?.search ? location?.search : ""}`,
                    });
                  }
                });
              setLoadingAdmin(false);
            } catch (e) {
              localStorage.removeItem("__extraconomy_token");
              localStorage.removeItem("__extraconomy_username");
              Navigate.replace("/login", {
                from: `${location.pathname}${location?.search ? location?.search : ""}`,
              });
            }
          }
        }
      } else {
        Navigate.replace("/login", {
          from: `${location.pathname}${location?.search ? location?.search : ""}`,
        });
      }
    }

    fetchData();

    return () => {
      cancel = true;
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [retryGetExtracoinInfo, setRetryGetExtracoinInfo] = useState(false);

  const handleRetryGetExtracoinInfo = () => setRetryGetExtracoinInfo(true);

  useEffect(() => {
    if (retryGetExtracoinInfo) {
      setRetryGetExtracoinInfo(false);
      setLoadingAdmin(true);
      getUserInfoService
        .getExtracoinInfo(
          localStorage.getItem("__extraconomy_token"),
          localStorage.getItem("__extraconomy_username")
        )
        .then((res) => {
          if (res.messageCode === 200001) setExtracoinInfo(res.result);
          else {
            localStorage.removeItem("__extraconomy_token");
            localStorage.removeItem("__extraconomy_username");
            Navigate.replace("/login", {
              from: `${location.pathname}${location?.search ? location?.search : ""}`,
            });
          }
        })
        .finally(() => setLoadingAdmin(false));
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryGetExtracoinInfo]);

  const onDocumentClick = () => {
    if (!topbarItemClick) {
      setActiveTopbarItem(null);
      setTopbarMenuActive(false);
    }

    if (!menuClick) {
      if (isHorizontal() || isSlim()) {
        setMenuActive(false);
      }
      hideOverlayMenu();
    }

    if (!inlineMenuClick && profileMode === "inline" && isSlim() && !isMobile()) {
      setInlineMenuActive(false);
    }

    if (configActive && !configClick) {
      setConfigActive(false);
    }

    inlineMenuClick = false;
    configClick = false;
    topbarItemClick = false;
    menuClick = false;
  };

  const onMenuitemClick = (event) => {
    if (!event.item.items) {
      hideOverlayMenu();

      if (isSlim() || isHorizontal()) {
        setMenuActive(false);
      }
    }
  };

  const onRootMenuitemClick = () => {
    setMenuActive((prevMenuActive) => !prevMenuActive);
  };

  const onMenuClick = () => {
    menuClick = true;

    if (inlineMenuActive && !inlineMenuClick) {
      setInlineMenuActive(false);
    }
  };

  const isMenuVisible = () => {
    if (isDesktop()) {
      if (menuMode === "static") return !staticMenuDesktopInactive;
      else if (menuMode === "overlay") return overlayMenuActive;
      else return true;
    } else {
      return true;
    }
  };

  const onMenuButtonClick = (event) => {
    menuClick = true;
    setTopbarMenuActive(false);

    if (isOverlay() && !isMobile()) {
      setOverlayMenuActive((prevOverlayMenuActive) => !prevOverlayMenuActive);
    } else {
      if (isDesktop()) {
        setStaticMenuDesktopInactive(
          (prevStaticMenuDesktopInactive) => !prevStaticMenuDesktopInactive
        );
      } else {
        setStaticMenuMobileActive((prevStaticMenuMobileActive) => !prevStaticMenuMobileActive);
      }
    }

    event?.preventDefault();
  };

  const onProfileButtonClick = (event) => {
    event.preventDefault();
    setInlineMenuActive((prevInlineMenuActive) => !prevInlineMenuActive);
    inlineMenuClick = true;

    if (isSlim() || isHorizontal()) {
      setMenuActive(false);
    }
  };

  const onTopbarMenuButtonClick = (event) => {
    event.preventDefault();
    topbarItemClick = true;
    setTopbarMenuActive((prevTopbarMenuActive) => !prevTopbarMenuActive);

    hideOverlayMenu();
  };

  const onTopbarItemClick = (event, item) => {
    event.preventDefault();
    topbarItemClick = true;

    if (activeTopbarItem === item) {
      setActiveTopbarItem(null);
    } else {
      setActiveTopbarItem(item);
    }
  };

  const hideOverlayMenu = () => {
    setOverlayMenuActive(false);
    setStaticMenuMobileActive(false);
  };

  const isDesktop = () => {
    return window.innerWidth > 896;
  };

  const isMobile = () => {
    return window.innerWidth <= 896;
  };

  const isOverlay = () => {
    return menuMode === "overlay";
  };

  const isHorizontal = () => {
    return menuMode === "horizontal";
  };

  const isSlim = () => {
    return menuMode === "slim";
  };

  const isStatic = () => {
    return menuMode === "static";
  };

  const hasInlineProfile = profileMode === "inline" && !isHorizontal();

  const containerClassName = classNames("layout-wrapper", {
    "layout-static": isStatic(),
    "layout-overlay": isOverlay(),
    "layout-overlay-active": overlayMenuActive,
    "layout-horizontal": isHorizontal(),
    "layout-slim": isSlim(),
    "layout-static-inactive": staticMenuDesktopInactive,
    "layout-mobile-active": staticMenuMobileActive,
    "layout-menu-dark": darkMenu,
    "layout-menu-light": !darkMenu,
    "p-input-filled": inputStyle === "filled",
    "p-ripple-disabled": !ripple,
  });

  const menuContainerClassName = classNames("layout-menu-container", {
    "layout-menu-container-inactive": !isMenuVisible(),
  });

  const routers = useMemo(
    () => [
      {
        path: "/dashboard-store-transactions",
        component: () => (
          <StoreTransactions user={userInfo} plan={userTypeInfo} currency={currency} />
        ),
        hasPermissionPlans: ["Store"],
      },
      {
        path: "/dashboard-store-pay",
        component: () => <QrCodePay user={userInfo} plan={userTypeInfo} currency={currency} />,
      },
      {
        path: "/dashboard-networkdirects",
        component: () => <NetworkTable users={directsArray} directs={true} plan={userTypeInfo} />,
      },
      {
        path: "/dashboard-networkall",
        component: () => <NetworkTable users={allNodesArray} directs={false} plan={userTypeInfo} />,
      },
      {
        path: "/dashboard-editprofile",
        component: () => (
          <EditProfileInfo user={userInfo} plan={userTypeInfo} setUserInfo={setUserInfo} />
        ),
      },
      {
        path: "/dashboard-walletStatement",
        component: () => <StatementTable currency={currency} plan={userTypeInfo} />,
      },
      {
        path: "/dashboard-extracoinStatement",
        component: () => <StatementTable currency={currency} plan={userTypeInfo} />,
      },
      {
        path: "/dashboard-extrabonusStatement",
        component: () => (
          <StatementTable currency={currency} plan={userTypeInfo} onlyIntegerValues={true} />
        ),
      },
      {
        path: "/dashboard-bonusStatement",
        component: () => <StatementTable bonus={true} currency={currency} plan={userTypeInfo} />,
      },
      {
        path: "/dashboard-dropshipping",
        component: () => <DropShipping getCategory={getCategory} />,
      },
      { path: "/dashboard-coupons", component: () => <Coupons plan={userTypeInfo} /> },
      {
        path: "/dashboard-products",
        component: () => (
          <ProductsList
            plan={userTypeInfo}
            userInfo={userInfo}
            currency={currency}
            imported={query.get("imported")}
          />
        ),
      },
      {
        path: "/dashboard-dropshippingproduct",
        component: () => <Product category={category} id={query.get("id")} />,
      },
      {
        path: "/dashboard-newproduct",
        component: () => (
          <NewProduct
            plan={userTypeInfo}
            currency={currency}
            userInfo={userInfo}
            extracoinInfo={extracoinInfo}
            handleRetryGetExtracoinInfo={handleRetryGetExtracoinInfo}
          />
        ),
      },
      { path: "/dashboard-attributes", component: () => <Attributes plan={userTypeInfo} /> },
      {
        path: "/dashboard-sales",
        component: () => <Orders sales={true} userInfo={userInfo} plan={userTypeInfo} />,
      },
      {
        path: "/dashboard-salesdetails/",
        component: () => <OrderDetails sales={true} userInfo={userInfo} plan={userTypeInfo} />,
      },
      {
        path: "/dashboard-purchases-evaluate/",
        component: () => <EvaluateOrder />,
      },
      {
        path: "/dashboard-purchases",
        component: () => <Orders sales={false} userInfo={userInfo} plan={userTypeInfo} />,
        exact: true,
      },
      {
        path: "/dashboard-purchasesdetails/",
        component: () => <OrderDetails sales={false} userInfo={userInfo} plan={userTypeInfo} />,
      },
      {
        path: "/dashboard-extracointransfer",
        component: () => (
          <ExtracoinTransfer
            currency={currency}
            extracoinInfo={extracoinInfo}
            userTypeInfo={userTypeInfo}
          />
        ),
      },
      {
        path: "/dashboard-extracoinintents",
        component: () => <ExtracoinIntents currency={currency} userTypeInfo={userTypeInfo} />,
      },
      {
        path: "/dashboard-withdrawintent",
        component: () => (
          <WithdrawIntent currency={currency} walletInfo={walletInfo} userTypeInfo={userTypeInfo} />
        ),
      },
      { path: "/dashboard-extrapay", component: () => <ExtraPay extracoinInfo={extracoinInfo} /> },
      {
        path: "/dashboard-userstore",
        component: () => <UserStore plan={userTypeInfo} currency={currency} />,
      },
      { path: "/dashboard-favorites", component: () => <FavoritedProducts /> },
      {
        path: "/dashboard-importproducts",
        component: () => <ImportProducts plan={userTypeInfo} />,
      },
      {
        path: "/dashboard-renewsubscription",
        component: () =>
          userHasSubscription ? (
            <RenewSubscription setSubscriptionInfo={setSubscriptionInfo} />
          ) : (
            <Redirect to="/dashboard" />
          ),
      },
      { path: "/dashboard-web3dashboard", component: () => <Web3Dashboard plan={userTypeInfo} /> },
      { path: "/dashboard-storesmap", component: () => <StoresMap plan={userTypeInfo} /> },
      {
        path: "/dashboard-fringe-benefit",
        component: () => <FringeBenefit currency={currency} />,
      },
      {
        path: "/dashboard-list-fringe-benefit",
        component: () => <FringeBenefitsList />,
      },
      {
        path: "/dashboard-edit-fringe-benefit",
        component: () => <FringeBenefitEdit />,
      },
      {
        path: "/dashboard-purchases-fringe-benefit",
        component: () => <FringeBenefitsPurchases />,
      },
      {
        path: "/dashboard-sales-fringe-benefit",
        component: () => <FringeBenefitsSales />,
      },
      {
        path: "/dashboard",
        component: () => (
          <UserDashboardHome
            walletInfo={walletInfo}
            extracoinInfo={extracoinInfo}
            userInfo={userInfo}
            userTypeInfo={userTypeInfo}
            currency={currency}
            directsInfo={directsInfo}
            allNodesInfo={allNodesInfo}
            subscriptionInfo={subscriptionInfo}
            activationInfo={activationInfo}
            lifetimeBonusInfo={lifetimeBonusInfo}
            extracoinPercentage={extracoinPercentage}
          />
        ),
        exact: true,
      },
    ],
    [
      activationInfo,
      allNodesArray,
      allNodesInfo,
      currency,
      directsArray,
      directsInfo,
      extracoinInfo,
      extracoinPercentage,
      lifetimeBonusInfo,
      category,
      query,
      subscriptionInfo,
      userInfo,
      userTypeInfo,
      walletInfo,
      userHasSubscription,
    ]
  );

  const {
    setTutorialState,
    tutorialState: { run, stepIndex, steps },
  } = useTutorialContext();

  useEffect(() => {
    if (localStorage.getItem("tutorial") === "true") return;
    else if (isDesktop() && (userTypeInfo === "Manager" || userTypeInfo === "Seller")) {
      setTutorialState({
        steps: stepsManagerAndSeller,
      });
    } else if (isMobile() && (userTypeInfo === "Manager" || userTypeInfo === "Seller")) {
      setTutorialState({
        steps: stepsManagerAndSellerMobile,
      });
    } else if (isDesktop() && userTypeInfo === "Partner") {
      setTutorialState({
        steps: stepsPartner,
      });
    } else if (isMobile() && userTypeInfo === "Partner") {
      setTutorialState({
        steps: stepsPartnerMobile,
      });
    }
  }, [setTutorialState, userTypeInfo]);

  const handleCallback = (data) => {
    localStorage.setItem("tutorial", "true");
    const { action, step } = data;

    if (action === "close") {
      setTutorialState((prevState) => ({
        ...prevState,
        run: false,
      }));
      return;
    }

    if (
      isMobile() &&
      data?.step?.target === "#step-5-mobile" &&
      data?.type === "step:after" &&
      steps.length === 7
    ) {
      isMenuVisible();
      onMenuButtonClick();
    } else if (
      isMobile() &&
      data?.action === "prev" &&
      data?.step?.target === "#step-6" &&
      data?.type === "step:after" &&
      steps.length === 7
    ) {
      isMenuVisible();
      onMenuButtonClick();
    }

    const { previous, next } = step?.data || {};
    const {
      index,

      type,
    } = data;
    const isPreviousAction = action === "prev";

    if (type === "step:after") {
      if (index < steps.length) {
        if (data?.action === "next")
          setTutorialState((prev) => ({ ...prev, stepIndex: index + 1 }));
        else if (data?.action === "prev")
          setTutorialState((prev) => ({ ...prev, stepIndex: index - 1 }));

        Navigate.push(isPreviousAction && previous ? previous : next);
      }

      if (index === steps.length) {
        if (isPreviousAction && previous) {
          setTutorialState((prev) => ({ ...prev, stepIndex: index - 1 }));
          Navigate.push(previous);
        } else {
          setTutorialState({ run: false, stepIndex: 0, tourActive: false });
        }
      }
    }
  };

  return (
    <div id="home">
      <Joyride
        callback={handleCallback}
        continuous
        run={run}
        stepIndex={stepIndex}
        steps={steps}
        styles={{
          options: {
            zIndex: 999,
            primaryColor: "#46a748",
          },
        }}
        disableOverlayClose
        showProgress
        tooltipComponent={TooltipComponent}
      />

      {loadingAdmin ? (
        <div className="flex justify-content-center align-items-center text-center min-h-screen surface-300">
          <i className="pi pi-spin pi-spinner text-8xl text-600"></i>
        </div>
      ) : (
        <>
          {isAdmin ? (
            <AdminBackoffice />
          ) : (
            <div className={containerClassName} onClick={onDocumentClick}>
              <ConfirmDocument />

              {subscriptionInfo && userHasSubscription && (
                <Dialog
                  header={`${localeText("popupHeader1")} ${
                    subscriptionInfo?.subscriptionDaysLeft
                  } ${localeText("popupHeader2")}`}
                  visible={isVisiblePopUp}
                  className="w-30rem m-2"
                  onHide={() => setNotVisiblePopUp()}
                  draggable={false}
                >
                  <p>
                    {localeText("subscriptionExpirationDate")}
                    {": "}
                    {formatDate(new Date(subscriptionInfo?.dateEnd))}
                  </p>
                  <p>{localeText("wantToRenewSubscription")}</p>
                  <div className="flex gap-2 justify-content-end">
                    <Button onClick={setNotVisiblePopUp} severity="danger">
                      {localeText("no")}
                    </Button>
                    <Button
                      onClick={() => {
                        Navigate.push("dashboard/renewSubscription");
                        setNotVisiblePopUp();
                      }}
                    >
                      <div className="flex justify-content-center align-items-center gap-2">
                        <p>{localeText("yes")}</p>
                      </div>
                    </Button>
                  </div>
                </Dialog>
              )}

              {subscriptionInfo && userHasSubscription && (
                <Dialog
                  header={localeText("yourSubcriptionExpired")}
                  visible={isVisiblePopUpExpired}
                  className="w-30rem m-2"
                  onHide={() => setNotVisiblePopUpExpired()}
                  draggable={false}
                  closable={false}
                >
                  <p>{localeText("wantToRenewyourSubscription")}</p>
                  <div className="flex gap-2 justify-content-end">
                    <Button onClick={() => Navigate.push("")} severity="danger">
                      {localeText("no")}
                    </Button>
                    <Button
                      onClick={() => {
                        Navigate.push("/dashboard-renewsubscription");
                        setNotVisiblePopUpExpired();
                      }}
                    >
                      <div className="flex justify-content-center align-items-center gap-2">
                        <p>{localeText("yes")}</p>
                      </div>
                    </Button>
                  </div>
                </Dialog>
              )}

              <Tooltip
                ref={copyTooltipRef}
                target=".block-action-copy"
                position="bottom"
                content="Copied to clipboard"
                event="focus"
              />

              {userInfo !== null && (
                <AppTopbar
                  topbarMenuActive={topbarMenuActive}
                  activeTopbarItem={activeTopbarItem}
                  onMenuButtonClick={onMenuButtonClick}
                  onTopbarMenuButtonClick={onTopbarMenuButtonClick}
                  onTopbarItemClick={onTopbarItemClick}
                  isHorizontal={isHorizontal()}
                  profileMode={profileMode}
                  isMobile={isMobile}
                  userInfo={userInfo}
                  currency={currency}
                  userTypeInfo={userTypeInfo}
                />
              )}

              <div className={menuContainerClassName} onClick={onMenuClick}>
                <div className="layout-menu-logo" id="step-1">
                  <button
                    className="p-link"
                    onClick={() =>
                      Navigate.push(
                        `${
                          JSON.parse(localStorage.getItem("__extraconomy_ismobile__"))
                            ? "/shop"
                            : "/"
                        }`
                      )
                    }
                  >
                    <img
                      id="layout-menu-logo"
                      src={logoWhiteHorizontal}
                      library="babylon-layout"
                      alt="babylon-logo"
                    />
                  </button>
                </div>
                <div className="layout-menu-wrapper">
                  <div className="menu-scroll-content">
                    {hasInlineProfile && (
                      <AppInlineProfile
                        inlineMenuActive={inlineMenuActive}
                        onProfileButtonClick={onProfileButtonClick}
                      />
                    )}
                    <AppMenu
                      model={menu}
                      menuMode={menuMode}
                      active={menuActive}
                      onMenuitemClick={onMenuitemClick}
                      onRootMenuitemClick={onRootMenuitemClick}
                    />
                  </div>
                </div>
              </div>

              <div className="layout-main" id="home">
                <div className="layout-content">
                  {routers.map((router, index) => {
                    // if (
                    //   router.path === "/dashboard-store/transactions" &&
                    //   !router?.hasPermissionPlans?.includes(userTypeInfo)
                    // )
                    //   return null;

                    return (
                      <Route
                        key={`router${index}`}
                        path={router.path}
                        component={router.component}
                        exact={router?.exact ? router.exact : false}
                      />
                    );
                  })}
                </div>
              </div>

              {staticMenuMobileActive && <div className="layout-mask"></div>}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default UserDashboard;
